import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, Dimmer, Form, Grid, Header, Icon, List, Loader, Message } from 'semantic-ui-react'
import ProfileSettingsService from '~/api/services/profileSettings'
import InfoPopup from '~/components/InfoPopup'
import notify from '~/components/Notify'
import { StyledSection, StyledMessage } from './style'
interface TwoFactorAuthenticationProps {
  qrSvg: string,
  data: any,
  handelGoogleAuth: (data: any, disable?: boolean) => void,
  handleSmsAuth: (data: any, disable?: boolean) => void
  isBusy: boolean
}

const TwoFactorAuthentication: React.FC<TwoFactorAuthenticationProps> = (props) => {
  const { t } = useTranslation()
  const { qrSvg, data, isBusy } = props
  const [generateSvg, setGenerateSvg] = useState(false)
  const [googleCode, setGoogleCode] = useState('')
  const [password, setPassword] = useState('')
  const [confirmationMobileCode, setConfirmationMobileCode] = useState('')
  const [viewPassword, setViewPassword] = useState(false)

  function renderHeader() {
    return <div style={{ display: 'flex', alignItems: 'baseline' }}>
      <Icon name='lock' size='large' color='blue' />
      <Header style={{ marginRight: 5 }} as={'h4'}>{t('Two Factor Authentication')}</Header>
      <InfoPopup
        content={t('Two-factor authentication (2FA) strengthens access security and protects your personal data from \n\
              unauthorized attacks by requiring two methods(also known as factors) to verify your identity.')}
        styleIcon={{ fontSize: '1.2em', marginLeft: 5, cursor: 'pointer' }}
      />
    </div>
  }

  function handelGoogleAuth(data, disable?) {
    props.handelGoogleAuth(data, disable)
  }

  async function handleSmsAuth(data, disable?) {
    props.handleSmsAuth(data, disable)
  }

  async function handleSendConfirmationSms() {
    try {
      if (data?.mobileConfirmed === 1) {
        await ProfileSettingsService.sendSmsCode()
      } else {
        await ProfileSettingsService.sendMobileConfirmationCode()
      }
      notify(t('Sms code sent'), 'success')
    } catch (error) {
      if (error.response.data) {
        notify(error.response.data.message, 'error')
      } else {
        notify(t('General error'), 'error')
      }
    }
  }

  function renderSvg() {
    const encodedData = window.btoa(qrSvg);
    return <>
      <StyledSection>
        <p>{t('Scan this barcode with your Google / Microsoft Authenticator App')}</p>
      </StyledSection>
      <StyledSection>
        <img src={`data:image/svg+xml;base64,${encodedData}`} alt='' />
      </StyledSection>
      <StyledSection>
        <Form>
          <Form.Input
            label={t('Authenticator Code')}
            placeholder={t('Authenticator Code')}
            onChange={(e, data) => setGoogleCode(data.value)}
          />
        </Form>
      </StyledSection>
      <Button primary onClick={() => handelGoogleAuth({ code: googleCode })}>{t('Enable 2FA')}</Button>
    </>
  }

  function renderEnabledGoogleAuth() {
    return <>
      <StyledSection>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <Icon name='lock' size='large' color='blue' />
          <Header as={'h4'}>{t('Google / Microsoft Authenticator')}</Header>
        </div>
      </StyledSection>
      <StyledSection>
        <StyledMessage positive>
          <Icon name='check circle outline' color='green' size='large' />
          <Message.Header>{t('2FA is currently enabled for your account')}</Message.Header>
        </StyledMessage>
      </StyledSection>
      <StyledSection>
        <span>{t('If you are looking to disable 2FA, please confirm your password and click Disable 2FA button.')}</span>
      </StyledSection>
      <StyledSection>
        <Form>
          <Form.Input
            type={viewPassword ? 'text' : 'password'}
            icon={
              <Icon
                link
                name='eye'
                onMouseDown={() => setViewPassword(!viewPassword)}
                onMouseUp={() => setViewPassword(!viewPassword)}
              />
            }
            label={t('Current password')}
            placeholder={t('Current password')}
            onChange={(e, data) => setPassword(data.value)}
          />
        </Form>
      </StyledSection>
      <Button onClick={() => handelGoogleAuth({ password }, true)} primary>{t('Disable 2FA')}</Button>
    </>
  }

  function renderDisabledGoogleContent() {
    return <>
      <StyledSection>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <Icon name='lock' size='large' color='blue' />
          <Header as={'h4'}>{t('Google / Microsoft Authenticator')}</Header>
        </div>
      </StyledSection>
      <StyledSection>
        <StyledMessage negative>
          <Icon name='info circle' color='red' size='large' />
          <Message.Header>{t('2FA is currently disabled for your account')}</Message.Header>
        </StyledMessage>
      </StyledSection>
      <StyledSection>
        <p>{t('To enable Two Factor Authentication on your account, you need to follow the steps below:')}</p>
        <List ordered relaxed>
          <List.Item>{t('Click on Generate Secret Button to generate a unique secret QR code for your profile')}</List.Item>
          <List.Item>{t('Verify the OTP from Google / Microsoft Authenticator Mobile App')}</List.Item>
        </List>
      </StyledSection>
      {data?.tfaType !== 'google' && <>
        {generateSvg ? renderSvg() : <Button onClick={() => setGenerateSvg(true)} primary>{t('Generate')}</Button>}
      </>
      }
    </>
  }

  function renderActiveSmsDisabledGoogle() {
    return <>
      <StyledSection>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <Icon name='lock' size='large' color='blue' />
          <Header as={'h4'}>{t('Google / Microsoft Authenticator')}</Header>
        </div>
      </StyledSection>
      <Message negative style={{ display: 'flex', alignItems: 'center' }}>
        <Icon name='info circle' color='red' size='large' />
        <div>{t('2FA via SMS is active. Cannot activate Google / Microsoft Authenticator')}</div>
      </Message>
    </>
  }

  function renderDisableGoogleAuth() {
    const activeSmsAuth = data?.tfaType === 'sms'
    return activeSmsAuth ? renderActiveSmsDisabledGoogle() : renderDisabledGoogleContent()
  }

  function renderGoogleAuthenticator() {
    const activeGoogleAuth = data?.tfaType === 'google'
    return <>
      {activeGoogleAuth ? renderEnabledGoogleAuth() : renderDisableGoogleAuth()}
    </>
  }

  function renderActivatedSmsAuth() {
    return <>
      <StyledSection>
        <StyledMessage positive>
          <Icon name='check circle outline' color='green' size='large' />
          <Message.Header>{t('2FA via SMS is currently enabled for your account')}</Message.Header>
        </StyledMessage>
      </StyledSection>
      <StyledSection>
        <Form>
          <Form.Input
            style={{ maxWidth: 300 }}
            label={t('Current password')}
            type={viewPassword ? 'text' : 'password'}
            icon={
              <Icon
                link
                name='eye'
                onMouseDown={() => setViewPassword(!viewPassword)}
                onMouseUp={() => setViewPassword(!viewPassword)}
              />
            }
            placeholder={t('Current password')}
            onChange={(e, data) => setPassword(data.value)}
          />
        </Form>
      </StyledSection>
      <Button onClick={() => handleSmsAuth({ password }, true)} primary>{t('Deactivate')}</Button>
    </>
  }

  function renderDeactiveSmsAuth() {
    const activeGoogleAuth = data?.tfaType === 'google'
    const number = `${data?.mobilePrefixCountryPhoneCode} ${data?.mobileNumber}`
    return <>
      {!activeGoogleAuth ? <>
        <StyledSection>
          <StyledMessage negative>
            <Icon name='info circle' color='red' size='large' />
            <Message.Header>{t('2FA via SMS is currently disabled for your account')}</Message.Header>
          </StyledMessage>
        </StyledSection>
        <StyledSection>
          <p>{t('To activate 2FA via SMS for your account, you have to take the following steps:')}</p>
          <List ordered relaxed>
            <List.Item>{t('Click the button to get a verification code to your mobile number {{number}}', { number })}</List.Item>
            <List.Item>{t('Enter the PIN code and then confirm your mobile number')}</List.Item>
          </List>
        </StyledSection>
        <StyledSection>
          <Button onClick={() => handleSendConfirmationSms()} primary>{t('Send new confirmation')}</Button>
        </StyledSection>
        <>
          <StyledSection>
            <Form>
              <Form.Input
                onChange={(e, data) => setConfirmationMobileCode(data.value)}
                label={t('Confirmation code')}
                placeholder={t('Confirmation code')}
              />
            </Form>
          </StyledSection>
        </>
        <Button onClick={() => handleSmsAuth({ code: confirmationMobileCode })} primary>{data?.mobileConfirmed === 0 ? t('Verify code') : t('Activate')}</Button>
      </> : <Message negative style={{ display: 'flex', alignItems: 'center' }}>
        <Icon name='info circle' color='red' size='large' />
        <div>{t('Google / Microsoft Authenticator Active. Cannot activate SMS 2FA')}</div>
      </Message>}
    </>
  }

  function renderSmsAuthenticator(activeSms) {
    return <>
      <StyledSection>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <Icon name='lock' size='large' color='blue' />
          <Header as={'h4'}>{t('SMS')}</Header>
        </div>
      </StyledSection>
      {activeSms ? renderActivatedSmsAuth() : renderDeactiveSmsAuth()}
    </>
  }

  function renderContent() {
    const activeSmsAuth = data?.tfaType === 'sms'
    return <Grid columns={2}>
      <Grid.Row>
        <Grid.Column>
          {renderGoogleAuthenticator()}
        </Grid.Column>
        <Grid.Column>
          {data?.smsActive > 0 && renderSmsAuthenticator(activeSmsAuth)}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  }

  return <div style={{ marginTop: 20, position: 'relative' }}>
    <Dimmer inverted active={isBusy}>
      <Loader active={isBusy} />
    </Dimmer>
    {renderHeader()}
    {renderContent()}
  </div>

}
export default TwoFactorAuthentication
